import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy, Check, ChevronDown, ExternalLink, Loader2, Send, Wrench, ArrowRight, X,
} from "lucide-react";
import { buildToolkitLinks, OFFICER_TOOLKITS } from "@/lib/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinkRow = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — open the link and copy from the address bar.");
    }
  };
  return (
    <li className="py-3.5 flex items-start gap-3" data-testid={`mlo-toolkit-link-${item.url}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-navy leading-snug">{item.label}</p>
        {item.use && <p className="mt-0.5 text-[12.5px] text-ink-500 leading-relaxed">{item.use}</p>}
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono text-teal hover:underline break-all"
        >
          {item.url} <ExternalLink size={10} className="shrink-0" />
        </a>
      </div>
      <button
        onClick={copy}
        data-testid="mlo-toolkit-copy"
        className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full border border-linen-border bg-white px-3 py-1.5 text-navy hover:border-teal transition-colors"
      >
        {copied ? <Check size={11} className="text-teal" /> : <Copy size={11} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </li>
  );
};

const HandoffForm = ({ toolkitKey, links, toolkitUrl, onDone, authHeader }) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/mlo/toolkits/send`,
        { toolkit: toolkitKey, to, links, toolkit_url: toolkitUrl, note },
        { headers: authHeader() },
      );
      toast.success(`Toolkit sent to ${to.trim()}`);
      setTo("");
      setNote("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl bg-linen/70 border border-linen-border p-3.5" data-testid={`mlo-toolkit-handoff-${toolkitKey}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-widest text-ink-500 font-semibold">Send this toolkit</p>
        <button onClick={onDone} className="p-1 text-ink-400 hover:text-navy transition-colors" aria-label="Close">
          <X size={13} />
        </button>
      </div>
      <p className="mt-1 text-[12px] text-ink-500 leading-relaxed">
        Your assistant, your marketing person, a BDM — they get the links, the context, and a page they can bookmark.
      </p>
      <input
        type="email"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder="assistant@example.com"
        data-testid={`mlo-toolkit-handoff-email-${toolkitKey}`}
        className="mt-2.5 w-full rounded-lg border border-linen-border bg-white px-3 py-2 text-sm text-navy outline-none focus:ring-2 focus:ring-teal/40"
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional one-liner — e.g. 'Use these for the open house on Saturday'"
        data-testid={`mlo-toolkit-handoff-note-${toolkitKey}`}
        className="mt-2 w-full rounded-lg border border-linen-border bg-white px-3 py-2 text-sm text-navy outline-none focus:ring-2 focus:ring-teal/40"
      />
      <button
        onClick={send}
        disabled={busy || !to.trim()}
        data-testid={`mlo-toolkit-handoff-send-${toolkitKey}`}
        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-navy text-white text-[12px] font-semibold px-4 py-2 hover:bg-[#173a5f] disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send toolkit
      </button>
    </div>
  );
};

const ToolkitSection = ({ tk, links, openKey, setOpenKey, authHeader }) => {
  const [handoff, setHandoff] = useState(false);
  const open = openKey === tk.key;
  const toolkitUrl = `${window.location.origin}/toolkit/${tk.key}`;

  return (
    <div className="border-b border-linen-border last:border-0" data-testid={`mlo-toolkit-${tk.key}`}>
      <button
        onClick={() => setOpenKey(open ? null : tk.key)}
        data-testid={`mlo-toolkit-toggle-${tk.key}`}
        className="w-full flex items-center gap-3 py-3.5 text-left group"
      >
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-navy group-hover:text-teal transition-colors">
            {tk.label}
          </span>
          <span className="block text-[12px] text-ink-500 truncate">
            {links.length} link{links.length !== 1 ? "s" : ""} · {tk.start_here?.split(".")[0]}.
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-ink-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="pb-5" data-testid={`mlo-toolkit-body-${tk.key}`}>
          <div className="rounded-xl bg-teal-light/40 border border-teal/20 px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest text-teal font-bold">Start here</p>
            <p className="mt-1 text-[13px] text-navy leading-relaxed">{tk.start_here}</p>
          </div>

          <ul className="mt-1 divide-y divide-linen-border">
            {links.length === 0 ? (
              <li className="py-4 text-[13px] text-ink-400">No links in this toolkit yet.</li>
            ) : (
              links.map((l) => <LinkRow key={l.url} item={l} />)
            )}
          </ul>

          {tk.steps?.length > 0 && (
            <div className="mt-2 rounded-xl bg-linen/60 px-4 py-3.5">
              <p className="text-[11px] uppercase tracking-widest text-ink-500 font-semibold">How to use these</p>
              <ol className="mt-2 space-y-2">
                {tk.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[12.5px] text-ink-600 leading-relaxed">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-white border border-linen-border text-teal text-[10px] font-bold inline-flex items-center justify-center">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <a
              href={`/toolkit/${tk.key}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`mlo-toolkit-page-${tk.key}`}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy hover:text-teal transition-colors"
            >
              Open as a page <ArrowRight size={12} />
            </a>
            <button
              onClick={() => setHandoff((v) => !v)}
              data-testid={`mlo-toolkit-handoff-open-${tk.key}`}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy hover:text-teal transition-colors"
            >
              <Send size={12} /> Send to my assistant
            </button>
          </div>

          {handoff && (
            <HandoffForm
              toolkitKey={tk.key}
              links={links}
              toolkitUrl={toolkitUrl}
              authHeader={authHeader}
              onDone={() => setHandoff(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default function ToolkitsCard({ authHeader }) {
  const [toolkits, setToolkits] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/toolkits`).then((r) => setToolkits(r.data || {})).catch(() => setToolkits({}));
  }, []);

  const origin = window.location.origin;
  const sections = useMemo(() => {
    if (!toolkits) return { everyday: [], pitching: [] };
    const build = (keys) =>
      keys
        .map((key) => toolkits[key])
        .filter(Boolean)
        .map((tk) => ({ tk, links: buildToolkitLinks({ key: tk.key, origin, apiBase: API }) }));
    return { everyday: build(OFFICER_TOOLKITS.everyday), pitching: build(OFFICER_TOOLKITS.pitching) };
  }, [toolkits, origin]);

  if (!toolkits) return null;

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6 sm:p-8" data-testid="mlo-toolkits-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Wrench size={13} /> Toolkits
      </div>
      <h2 className="mt-1 font-heading font-medium text-2xl text-navy">Which link do I send?</h2>
      <p className="mt-1 text-sm text-ink-500">
        Every page we've built, grouped by who you're talking to — with a note on each one telling you
        when to use it. Open a toolkit, copy the link, send it.
      </p>

      <div className="mt-5 divide-y divide-linen-border border-t border-linen-border">
        {sections.everyday.map(({ tk, links }) => (
          <ToolkitSection key={tk.key} tk={tk} links={links} openKey={openKey} setOpenKey={setOpenKey} authHeader={authHeader} />
        ))}
      </div>

      {sections.pitching.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-widest text-ink-400 font-semibold">
            Pitching a team leader or brokerage?
          </p>
          <div className="mt-1.5 divide-y divide-linen-border border-t border-linen-border">
            {sections.pitching.map(({ tk, links }) => (
              <ToolkitSection key={tk.key} tk={tk} links={links} openKey={openKey} setOpenKey={setOpenKey} authHeader={authHeader} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
